// src/services/recallService.ts
// ─────────────────────────────────────────────────────────────────
// Recall Service — Phase 2 (Mock)
//
// Phase 2 additions over Phase 1:
//   • resendRecallNotification() — push reminder to unacknowledged crew
//   • writeJobEvent()            — writes recall event to Jobs register
//   • getRecallExportHtml()      — generates branded HTML for print-to-PDF
//
// The recall log is APPEND-ONLY — no edit or delete exists.
// This is a legal/contractual record (show-cause documentation).
// At beta: recall_events DB table has RLS DELETE = DENY for all tiers.
//
// At beta: replace function bodies with fetch('/api/recall/...')
// ─────────────────────────────────────────────────────────────────

import type {
  RecallEvent, RecallReasonLabel, RecallCrewStatus,
} from '../types/mobileCrewTypes'
import { addCrewNotification }   from '../utils/mockDataNotificationBus'
import { addJobEvent }           from '../utils/mockDataJobEvents'
import { buildDocumentHeader }   from '../utils/documentBranding'
import { getDocumentBranding }   from '../utils/documentBranding'

// ── Module-level store (append-only) ──────────────────────────────
const _recallLog: RecallEvent[] = []
let _recallSeq: Record<string, number> = {}

function nextRecallId(jobNumber: string): string {
  _recallSeq[jobNumber] = (_recallSeq[jobNumber] ?? 0) + 1
  return `RC-${jobNumber}-${String(_recallSeq[jobNumber]).padStart(3, '0')}`
}

// ── Initiate recall ────────────────────────────────────────────────
export interface InitiateRecallInput {
  jobIds: string[]
  jobNumbers: string[]
  reason: RecallReasonLabel
  freeText: string | null
  musterPoint: string | null
  returnETA: string | null
  urgency: 'IMMEDIATE' | 'END_OF_TASK' | 'END_OF_SHIFT'
  affectedCrew: Array<{
    employeeId: string
    employeeName: string
    role: string
    currentStatus: string
  }>
  issuedById: string
  issuedByName: string
  issuedByTier: string
}

export async function initiateRecall(input: InitiateRecallInput): Promise<RecallEvent> {
  // Phase 6: POST /api/recall
  const now = new Date()
  const primaryJob = input.jobNumbers[0] ?? 'SITE'
  const recallId = nextRecallId(primaryJob)

  const crewStatuses: RecallCrewStatus[] = input.affectedCrew.map(c => ({
    employeeId:       c.employeeId,
    employeeName:     c.employeeName,
    role:             c.role,
    statusAtRecall:   c.currentStatus,
    recallSentAt:     now.toISOString(),
    acknowledgedAt:   null,
    returnedToSiteAt: null,
  }))

  const event: RecallEvent = {
    id:                   recallId,
    sequence:             _recallSeq[primaryJob],
    jobIds:               input.jobIds,
    jobNumbers:           input.jobNumbers,
    reason:               input.reason,
    freeText:             input.freeText,
    musterPoint:          input.musterPoint,
    returnETA:            input.returnETA,
    urgency:              input.urgency,
    affectedCrew:         crewStatuses,
    issuedById:           input.issuedById,
    issuedByName:         input.issuedByName,
    issuedByTier:         input.issuedByTier,
    issuedAtAEST:         now.toLocaleString('sv', { timeZone: 'Australia/Brisbane' }).replace(' ', 'T'),
    issuedAtUTC:          now.toISOString(),
    allReturnedAt:        null,
    totalShutdownMinutes: null,
    exportedAt:           null,
    exportedById:         null,
    exportedByName:       null,
    immutable:            true,
  }

  _recallLog.push(event)

  // 2B-04: Dispatch push notification to all affected crew via notification bus
  addCrewNotification({
    type:          'INCIDENT_ALERT',
    priority:      'CRITICAL',
    employeeIds:   input.affectedCrew.map(c => c.employeeId),
    jobIds:        input.jobIds,
    heading:       `SITE RECALL — ${input.reason}`,
    body:          [
      input.urgency === 'IMMEDIATE' ? 'IMMEDIATE: Leave the site now.' :
      input.urgency === 'END_OF_TASK' ? 'Complete your current task then depart.' :
      'Return home at normal shift end.',
      input.freeText || '',
      input.musterPoint ? `Muster at: ${input.musterPoint}` : '',
      input.returnETA   ? `Estimated return: ${input.returnETA}` : '',
    ].filter(Boolean).join(' '),
    requiredAction: 'Tap ACKNOWLEDGED to confirm you have received this recall.',
    sourceModule:   'recallService',
    sourceRecordId: recallId,
    requiresAck:    true,
    payload: {
      recallId,
      reason:      input.reason,
      urgency:     input.urgency,
      musterPoint: input.musterPoint,
      returnETA:   input.returnETA,
    },
  })

  // 2C-05: Write event to Jobs / Project Register Events log
  await writeJobEvent(event)

  return event
}

/** Record crew acknowledgement. */
export async function acknowledgeRecall(
  recallId: string,
  employeeId: string,
): Promise<RecallEvent | null> {
  // Phase 6: PATCH /api/recall/:id/acknowledge
  const event = _recallLog.find(r => r.id === recallId)
  if (!event) return null
  const idx = event.affectedCrew.findIndex(c => c.employeeId === employeeId)
  if (idx >= 0 && !event.affectedCrew[idx].acknowledgedAt) {
    event.affectedCrew[idx].acknowledgedAt = new Date().toISOString()
  }
  return event
}

/** Record crew returning to site after recall. */
export async function recordReturnToSite(
  recallId: string,
  employeeId: string,
): Promise<RecallEvent | null> {
  // Phase 6: PATCH /api/recall/:id/return
  const event = _recallLog.find(r => r.id === recallId)
  if (!event) return null
  const idx = event.affectedCrew.findIndex(c => c.employeeId === employeeId)
  if (idx >= 0) {
    event.affectedCrew[idx].returnedToSiteAt = new Date().toISOString()
  }
  const allReturned = event.affectedCrew.every(c => c.returnedToSiteAt)
  if (allReturned) {
    const lastReturn = Math.max(
      ...event.affectedCrew.map(c => new Date(c.returnedToSiteAt!).getTime())
    )
    event.allReturnedAt = new Date(lastReturn).toISOString()
    const issued = new Date(event.issuedAtUTC).getTime()
    event.totalShutdownMinutes = Math.round((lastReturn - issued) / 60_000)

    // Write resolved event to Jobs register
    addJobEvent(event.jobIds[0] ?? '', event.jobNumbers[0] ?? '', {
      type:          'RECALL_RESOLVED',
      title:         `Recall resolved — ${event.id}`,
      description:   `All ${event.affectedCrew.length} crew have returned to site. Total shutdown: ${event.totalShutdownMinutes} minutes.`,
      refId:         event.id,
      refType:       'RECALL',
      createdById:   event.issuedById,
      createdByName: event.issuedByName,
      severity:      'INFO',
    })
  }
  return event
}

/** 2C-05 — Write recall event to the Jobs / Project Register Events log. */
async function writeJobEvent(event: RecallEvent): Promise<void> {
  // Phase 6: POST /api/jobs/:id/events (called internally after initiateRecall)
  const description = [
    `Recall reason: ${event.reason}`,
    event.freeText       ? `Detail: ${event.freeText}` : null,
    event.musterPoint    ? `Muster point: ${event.musterPoint}` : null,
    `Crew affected: ${event.affectedCrew.map(c => c.employeeName).join(', ')}`,
    `Urgency: ${event.urgency}`,
    `Issued by: ${event.issuedByName} (${event.issuedByTier})`,
  ].filter(Boolean).join('. ')

  // Write one event per affected job
  event.jobIds.forEach((jobId, i) => {
    addJobEvent(jobId, event.jobNumbers[i] ?? '', {
      type:          'SITE_RECALL',
      title:         `Site recall issued — ${event.id}`,
      description,
      refId:         event.id,
      refType:       'RECALL',
      createdById:   event.issuedById,
      createdByName: event.issuedByName,
      severity:      'CRITICAL',
    })
  })
}

/**
 * 2B-05 / 2C-04 — Resend recall push notification to all unacknowledged crew.
 * Called when PM clicks "Resend Reminder" in the Recall Status panel.
 */
export async function resendRecallNotification(recallId: string): Promise<number> {
  // Phase 6: POST /api/recall/:id/resend
  const event = _recallLog.find(r => r.id === recallId)
  if (!event) return 0

  const unacknowledged = event.affectedCrew.filter(c => !c.acknowledgedAt)
  if (!unacknowledged.length) return 0

  addCrewNotification({
    type:          'INCIDENT_ALERT',
    priority:      'CRITICAL',
    employeeIds:   unacknowledged.map(c => c.employeeId),
    jobIds:        event.jobIds,
    heading:       `RECALL REMINDER — ${event.id} — Please acknowledge`,
    body:          `This is a reminder that a site recall is active: ${event.reason}. Please tap ACKNOWLEDGED immediately.`,
    requiredAction:'Tap ACKNOWLEDGED now. PM has been notified you have not yet responded.',
    sourceModule:  'recallService',
    sourceRecordId: recallId,
    requiresAck:   true,
    payload:       { recallId, isReminder: true },
  })

  return unacknowledged.length
}

/** Get all recall events, most-recent-first. */
export async function getRecallLog(jobId?: string): Promise<RecallEvent[]> {
  // Phase 6: GET /api/recall?jobId=...
  const results = jobId
    ? _recallLog.filter(r => r.jobIds.includes(jobId))
    : [..._recallLog]
  return Promise.resolve(results.slice().reverse())
}

/** Any currently active (unresolved) recalls. */
export async function getActiveRecalls(): Promise<RecallEvent[]> {
  // Phase 6: GET /api/recall?status=active
  return Promise.resolve(_recallLog.filter(r => r.allReturnedAt === null))
}

/** Single recall by ID. */
export async function getRecallById(id: string): Promise<RecallEvent | null> {
  // Phase 6: GET /api/recall/:id
  return Promise.resolve(_recallLog.find(r => r.id === id) ?? null)
}

/**
 * 2C-03 — Generate branded HTML for the recall notice (print-to-PDF).
 * Uses documentBranding.ts buildDocumentHeader('RECALL_NOTICE', id).
 * At beta this function is unchanged — the HTML it produces is the same
 * whether data comes from mock store or API.
 */
export async function getRecallExportHtml(recallId: string): Promise<string | null> {
  // Phase 6: GET /api/recall/:id/export-html (returns same HTML shape)
  const event = _recallLog.find(r => r.id === recallId)
  if (!event) return null

  const branding   = getDocumentBranding()
  const headerHtml = buildDocumentHeader('RECALL_NOTICE', event.id)

  const acked    = event.affectedCrew.filter(c => c.acknowledgedAt).length
  const returned = event.affectedCrew.filter(c => c.returnedToSiteAt).length
  const pending  = event.affectedCrew.length - acked

  const crewRows = event.affectedCrew.map(c => `
    <tr>
      <td>${c.employeeName}</td>
      <td>${c.role}</td>
      <td>${c.statusAtRecall}</td>
      <td>${c.acknowledgedAt ? new Date(c.acknowledgedAt).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' }) : '—'}</td>
      <td>${c.returnedToSiteAt ? new Date(c.returnedToSiteAt).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' }) : '—'}</td>
    </tr>
  `).join('')

  const durationStr = event.totalShutdownMinutes != null
    ? `${Math.floor(event.totalShutdownMinutes / 60)}h ${event.totalShutdownMinutes % 60}m`
    : 'Ongoing'

  const accent = branding.docTypeOverrides['RECALL_NOTICE']?.accentColor ?? branding.accentColor

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Site Recall Notice — ${event.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: ${branding.fontFamily}, Arial, sans-serif; font-size: 10.5pt; color: #1a1a1a; padding: 20mm; }
    h2 { font-size: 16pt; color: ${accent}; border-bottom: 2px solid ${accent}; padding-bottom: 6px; margin: 16px 0 12px; }
    h3 { font-size: 11pt; color: #333; margin: 14px 0 6px; text-transform: uppercase; letter-spacing: 0.5px; }
    table.detail { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    table.detail td { padding: 5px 10px; vertical-align: top; }
    table.detail td:first-child { font-weight: 600; color: #555; width: 38%; }
    table.crew { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-top: 8px; }
    table.crew th { background: ${accent}; color: #fff; font-weight: 700; padding: 7px 10px; text-align: left; }
    table.crew td { padding: 6px 10px; border-bottom: 1px solid #e5e5e5; }
    table.crew tr:nth-child(even) td { background: #f7f7f7; }
    .stat-row { display: flex; gap: 20px; margin-bottom: 14px; }
    .stat { text-align: center; padding: 10px 20px; border: 1px solid #ddd; border-radius: 6px; min-width: 90px; }
    .stat .val { font-size: 22pt; font-weight: 900; color: ${accent}; }
    .stat .lbl { font-size: 8pt; color: #888; margin-top: 2px; }
    .critical-badge { display: inline-block; background: #C0392B; color: #fff;
      font-weight: 900; font-size: 9pt; padding: 3px 10px; border-radius: 4px;
      letter-spacing: 1px; margin-bottom: 8px; }
    .footer { margin-top: 24px; border-top: 1px solid #ddd; padding-top: 10px;
      font-size: 8pt; color: #999; text-align: center; }
    @media print { body { padding: 15mm; } }
  </style>
</head>
<body>
  ${headerHtml}

  <h2>Site Recall Notice</h2>
  <div class="critical-badge">OFFICIAL RECORD — DO NOT ALTER</div>

  <table class="detail">
    <tr><td>Recall ID</td><td><strong>${event.id}</strong></td></tr>
    <tr><td>Issued at (AEST)</td><td>${new Date(event.issuedAtAEST).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', weekday:'long', year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit' })}</td></tr>
    <tr><td>Issued at (UTC)</td><td>${event.issuedAtUTC}</td></tr>
    <tr><td>Issued by</td><td>${event.issuedByName} (${event.issuedByTier})</td></tr>
    <tr><td>Job(s) affected</td><td>${event.jobNumbers.join(', ')}</td></tr>
    <tr><td>Recall reason</td><td><strong>${event.reason}</strong></td></tr>
    ${event.freeText ? `<tr><td>Additional detail</td><td>${event.freeText}</td></tr>` : ''}
    <tr><td>Urgency</td><td>${event.urgency}</td></tr>
    ${event.musterPoint ? `<tr><td>Muster point</td><td>${event.musterPoint}</td></tr>` : ''}
    ${event.returnETA   ? `<tr><td>Return to site ETA</td><td>${event.returnETA}</td></tr>` : ''}
    <tr><td>Total shutdown duration</td><td>${durationStr}</td></tr>
    ${event.allReturnedAt ? `<tr><td>All crew returned at</td><td>${new Date(event.allReturnedAt).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' })}</td></tr>` : ''}
  </table>

  <div class="stat-row">
    <div class="stat"><div class="val">${event.affectedCrew.length}</div><div class="lbl">Crew affected</div></div>
    <div class="stat"><div class="val" style="color:#1E8449;">${acked}</div><div class="lbl">Acknowledged</div></div>
    <div class="stat"><div class="val" style="color:${pending > 0 ? '#D4AC0D' : '#888'};">${pending}</div><div class="lbl">Pending ack</div></div>
    <div class="stat"><div class="val">${returned}</div><div class="lbl">Returned to site</div></div>
  </div>

  <h3>Crew status at time of recall</h3>
  <table class="crew">
    <thead>
      <tr>
        <th>Name</th><th>Role</th><th>Status at recall</th>
        <th>Acknowledged</th><th>Returned to site</th>
      </tr>
    </thead>
    <tbody>${crewRows}</tbody>
  </table>

  <p style="font-size:8.5pt;color:#666;margin-top:14px;border-left:3px solid ${accent};padding-left:10px;">
    This document is an official site recall record generated by TrafficProcure MCBCE-K.
    It forms part of the project audit trail and may be used as show-cause evidence in
    extension-of-time claims, client delay notices, or regulatory investigations.
    This record must not be altered after generation.
  </p>

  <div class="footer">
    ${branding.companyName} · ABN ${branding.abn} · Recall Notice ${event.id} ·
    Generated ${new Date().toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' })} ·
    ${branding.footerCustomText || 'Confidential'}
  </div>
</body>
</html>`
}

/** Stats helper used by the UI recall status panel. */
export function getRecallStats(event: RecallEvent) {
  const acked    = event.affectedCrew.filter(c => c.acknowledgedAt).length
  const returned = event.affectedCrew.filter(c => c.returnedToSiteAt).length
  const pending  = event.affectedCrew.length - acked
  let duration: string | null = null
  if (event.totalShutdownMinutes != null) {
    const h = Math.floor(event.totalShutdownMinutes / 60)
    const m = event.totalShutdownMinutes % 60
    duration = h > 0 ? `${h}h ${m}m` : `${m}m`
  } else if (event.issuedAtUTC) {
    const mins = Math.round((Date.now() - new Date(event.issuedAtUTC).getTime()) / 60_000)
    const h = Math.floor(mins / 60)
    const m = mins % 60
    duration = `${h > 0 ? h + 'h ' : ''}${m}m (ongoing)`
  }
  return { total: event.affectedCrew.length, acked, pending, returned, duration }
}
